import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  setDoc, 
  addDoc, 
  updateDoc, 
  orderBy 
} from 'firebase/firestore';
import { db } from './firebase';
import { 
  MockExam, 
  MockAttempt, 
  Question, 
  ConstructedResponsePrompt,
  UnofficialScoreReport 
} from '../types';

export interface MockLoadSuccess {
  success: true;
  mockExam: MockExam;
  questions: Question[];
  constructedResponses: ConstructedResponsePrompt[];
}

export interface MockLoadDataError {
  success: false;
  mockExam: MockExam;
  errorType: 'DATA_ERROR';
  missingId: string;
  itemType: 'question' | 'constructed_response';
  message: string;
}

export interface MockLoadNotFound {
  success: false;
  mockExam?: undefined;
  errorType: 'NOT_FOUND';
  missingId: string;
  itemType: 'mock_exam';
  message: string;
}

export type MockLoadResult = MockLoadSuccess | MockLoadDataError | MockLoadNotFound;

/**
 * Fetches all published mock exams for a given paper ID from Firestore.
 * Draft and retired mocks are strictly filtered out for student-facing views.
 */
export async function getPublishedMockExamsForPaper(paperId: string): Promise<MockExam[]> {
  try {
    const colRef = collection(db, 'mockExams');
    const q = query(
      colRef,
      where('paperId', '==', paperId),
      where('status', '==', 'published')
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as MockExam));
    }
  } catch (err) {
    console.warn(`Error fetching published mock exams for paper ${paperId}:`, err);
  }
  return [];
}

/**
 * Fetches all published mock exams across all exam papers and families from Firestore.
 * Draft and retired mocks are strictly filtered out for student-facing views.
 */
export async function getAllPublishedMockExams(): Promise<MockExam[]> {
  try {
    const colRef = collection(db, 'mockExams');
    const q = query(
      colRef,
      where('status', '==', 'published')
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as MockExam));
    }
  } catch (err) {
    console.warn('Error fetching all published mock exams:', err);
  }
  return [];
}

/**
 * Retrieves a specific MockExam document by its ID.
 */
export async function getMockExamById(mockExamId: string): Promise<MockExam | null> {
  try {
    const docRef = doc(db, 'mockExams', mockExamId);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return { id: snap.id, ...snap.data() } as MockExam;
    }
  } catch (err) {
    console.error(`Error retrieving mock exam ${mockExamId}:`, err);
  }
  return null;
}

/**
 * Loads mock exam content by fetching EACH locked question document by exact ID in parallel.
 * 
 * CRITICAL INVARIANTS:
 * 1. Must load exact document IDs in `MockExam.questionIds[]` without querying domains.
 * 2. If ANY ID in `questionIds[]` or `constructedResponsePromptIds[]` fails to resolve,
 *    returns an explicit DATA_ERROR (do not fall back to "content in preparation").
 */
export async function loadMockExamContent(mockExam: MockExam): Promise<MockLoadResult> {
  const paperId = mockExam.paperId;

  // 1. Fetch all MC Questions by exact document ID in parallel
  const questionSnaps = await Promise.all(
    mockExam.questionIds.map(async (qId) => {
      try {
        const qRef = doc(db, 'praxisPapers', paperId, 'questions', qId);
        const qSnap = await getDoc(qRef);
        return { qId, exists: qSnap.exists(), data: qSnap.exists() ? qSnap.data() : null };
      } catch (err) {
        return { qId, exists: false, data: null };
      }
    })
  );

  // Check for any unresolvable question ID
  for (const item of questionSnaps) {
    if (!item.exists || !item.data) {
      return {
        success: false,
        mockExam,
        errorType: 'DATA_ERROR',
        missingId: item.qId,
        itemType: 'question',
        message: `Referenced Question ID "${item.qId}" could not be retrieved from praxisPapers/${paperId}/questions. This mock contains an unresolvable item reference.`
      };
    }
  }

  const loadedQuestions: Question[] = questionSnaps.map(item => ({
    id: item.qId,
    paperId,
    ...item.data
  } as Question));

  // 2. Fetch Constructed Response Prompts if mock has them
  const loadedCRPrompts: ConstructedResponsePrompt[] = [];
  if (Array.isArray(mockExam.constructedResponsePromptIds) && mockExam.constructedResponsePromptIds.length > 0) {
    // Read parent paper to access constructedResponsePrompts array
    const paperRef = doc(db, 'praxisPapers', paperId);
    const paperSnap = await getDoc(paperRef);
    
    if (!paperSnap.exists()) {
      return {
        success: false,
        mockExam,
        errorType: 'DATA_ERROR',
        missingId: paperId,
        itemType: 'constructed_response',
        message: `Parent paper document "praxisPapers/${paperId}" does not exist to resolve constructed response prompts.`
      };
    }

    const paperData = paperSnap.data();
    const availableCRs: ConstructedResponsePrompt[] = Array.isArray(paperData?.constructedResponsePrompts)
      ? paperData.constructedResponsePrompts
      : [];

    for (const crId of mockExam.constructedResponsePromptIds) {
      const matched = availableCRs.find(cr => cr.id === crId);
      if (!matched) {
        return {
          success: false,
          mockExam,
          errorType: 'DATA_ERROR',
          missingId: crId,
          itemType: 'constructed_response',
          message: `Referenced Constructed Response Prompt ID "${crId}" was not found in praxisPapers/${paperId}.constructedResponsePrompts.`
        };
      }
      loadedCRPrompts.push(matched);
    }
  }

  return {
    success: true,
    mockExam,
    questions: loadedQuestions,
    constructedResponses: loadedCRPrompts
  };
}

/**
 * Initializes a new MockAttempt document in Firestore.
 */
export async function createMockAttempt(
  userId: string,
  mockExamId: string,
  paperId: string
): Promise<string> {
  const attemptId = `mock_att_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
  const attempt: MockAttempt = {
    id: attemptId,
    userId: userId || 'guest_user_1',
    mockExamId,
    paperId,
    startedAt: new Date().toISOString(),
    completedAt: null,
    status: 'in_progress',
    userAnswers: {},
    constructedResponses: {},
    timeSpentSeconds: 0
  };

  try {
    const docRef = doc(db, 'mockAttempts', attemptId);
    await setDoc(docRef, attempt);
    return attemptId;
  } catch (err) {
    console.error('Error saving new MockAttempt to Firestore:', err);
    return attemptId;
  }
}

/**
 * Updates an ongoing or completed MockAttempt in Firestore.
 */
export async function updateMockAttempt(
  attemptId: string,
  updates: Partial<MockAttempt>
): Promise<void> {
  try {
    const docRef = doc(db, 'mockAttempts', attemptId);
    await setDoc(docRef, updates, { merge: true });
  } catch (err) {
    console.warn(`Error updating MockAttempt ${attemptId}:`, err);
  }
}

/**
 * Fetches user's previous mock attempts for a given paper or mock exam.
 */
export async function getUserMockAttempts(
  userId: string,
  paperId?: string
): Promise<MockAttempt[]> {
  if (!userId || userId === 'guest_user_1') return [];
  try {
    const colRef = collection(db, 'mockAttempts');
    let q = query(colRef, where('userId', '==', userId));
    if (paperId) {
      q = query(colRef, where('userId', '==', userId), where('paperId', '==', paperId));
    }
    const snap = await getDocs(q);
    if (!snap.empty) {
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as MockAttempt));
    }
  } catch (err) {
    console.warn('Error fetching user mock attempts:', err);
  }
  return [];
}

/**
 * Generates an UnofficialScoreReport from a completed MockAttempt and its locked MockExam snapshot.
 * Fetches question documents by exact IDs from the attempt.
 */
export async function generateUnofficialScoreReport(
  mockAttempt: MockAttempt,
  mockExam: MockExam
): Promise<UnofficialScoreReport> {
  const userAnswers = mockAttempt.userAnswers || {};
  const questionIds = Object.keys(userAnswers);

  // Fetch each question by exact ID to retrieve correctAnswerIndex and domain
  const questionDocs = await Promise.all(
    questionIds.map(async (qId) => {
      try {
        const qRef = doc(db, 'praxisPapers', mockAttempt.paperId, 'questions', qId);
        const qSnap = await getDoc(qRef);
        if (qSnap.exists()) {
          const d = qSnap.data();
          return {
            id: qSnap.id,
            correctAnswerIndex: d.correctAnswerIndex ?? 0,
            domain: d.domain || d.domainName || 'General Content'
          };
        }
      } catch (err) {
        console.warn(`Error fetching question ${qId} for report scoring:`, err);
      }
      return {
        id: qId,
        correctAnswerIndex: -1,
        domain: 'General Content'
      };
    })
  );

  let totalCorrect = 0;
  const totalQuestions = questionDocs.length;

  // Domain map initialized from the locked MockExam's domainCompositionSnapshot
  const domainMap = new Map<string, { correct: number; total: number }>();
  if (mockExam.domainCompositionSnapshot && Array.isArray(mockExam.domainCompositionSnapshot)) {
    mockExam.domainCompositionSnapshot.forEach((d) => {
      const dName = (d as any).domainName || d.name;
      if (dName) {
        domainMap.set(dName, { correct: 0, total: 0 });
      }
    });
  }

  questionDocs.forEach((q) => {
    const studentAnswer = userAnswers[q.id];
    const isCorrect = studentAnswer !== undefined && studentAnswer === q.correctAnswerIndex;
    if (isCorrect) {
      totalCorrect++;
    }

    const domainName = q.domain;
    if (!domainMap.has(domainName)) {
      domainMap.set(domainName, { correct: 0, total: 0 });
    }
    const currentDomain = domainMap.get(domainName)!;
    currentDomain.total++;
    if (isCorrect) {
      currentDomain.correct++;
    }
  });

  const percentCorrect = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 10000) / 100 : 0;

  const domainBreakdown = Array.from(domainMap.entries()).map(([name, stats]) => ({
    domainName: name,
    correct: stats.correct,
    total: stats.total,
    percentCorrect: stats.total > 0 ? Math.round((stats.correct / stats.total) * 10000) / 100 : 0
  }));

  // Estimated Scaled Score Calculation
  let estimatedScaledScore: number | null = null;
  let estimatedReadiness: 'likely_pass' | 'borderline' | 'likely_not_pass' | 'unavailable' = 'unavailable';

  const minScale = mockExam.scoreScaleMinSnapshot;
  const maxScale = mockExam.scoreScaleMaxSnapshot;
  const passingScore = mockExam.passingScoreSnapshot;

  if (minScale !== null && minScale !== undefined && maxScale !== null && maxScale !== undefined) {
    const rawPercentRatio = totalQuestions > 0 ? totalCorrect / totalQuestions : 0;
    estimatedScaledScore = Math.round(minScale + rawPercentRatio * (maxScale - minScale));

    if (passingScore !== null && passingScore !== undefined) {
      if (estimatedScaledScore >= passingScore + 10) {
        estimatedReadiness = 'likely_pass';
      } else if (estimatedScaledScore >= passingScore - 10) {
        estimatedReadiness = 'borderline';
      } else {
        estimatedReadiness = 'likely_not_pass';
      }
    }
  }

  // AI-Assisted Constructed Response Scoring (Phase 6)
  let constructedResponseEstimates: UnofficialScoreReport['constructedResponseEstimates'] = undefined;
  if (mockAttempt.constructedResponses && Object.keys(mockAttempt.constructedResponses).length > 0) {
    const crEntries = Object.entries(mockAttempt.constructedResponses);
    
    // Fetch paper metadata and prompts if available
    let availableCRs: ConstructedResponsePrompt[] = [];
    let paperTitle = mockExam.paperId;
    try {
      const paperRef = doc(db, 'praxisPapers', mockAttempt.paperId);
      const paperSnap = await getDoc(paperRef);
      if (paperSnap.exists()) {
        const pData = paperSnap.data();
        paperTitle = pData.title || paperTitle;
        if (Array.isArray(pData.constructedResponsePrompts)) {
          availableCRs = pData.constructedResponsePrompts;
        }
      }
    } catch (pErr) {
      console.warn('Could not load parent paper metadata for CR scoring:', pErr);
    }

    // Evaluate each prompt response
    constructedResponseEstimates = await Promise.all(
      crEntries.map(async ([promptId, responseText]) => {
        const matchedPrompt = availableCRs.find(cr => cr.id === promptId);
        const promptText = matchedPrompt?.promptText || `Constructed Response Prompt (${promptId})`;
        const rubricNotes = matchedPrompt?.rubricNotes || '';
        const domain = matchedPrompt?.domain || 'General Competency';

        try {
          const evalRes = await fetch('/api/ai/evaluate-cr', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              promptText,
              responseText,
              rubricNotes,
              domain,
              paperName: paperTitle
            })
          });

          if (evalRes.ok) {
            const data = await evalRes.json();
            const score = typeof data.score === 'number' ? data.score : null;
            const scaleMin = typeof data.scaleMin === 'number' ? data.scaleMin : 0;
            const scaleMax = typeof data.scaleMax === 'number' ? data.scaleMax : 4;
            const confidence = (['high', 'medium', 'low'].includes(data.confidence) ? data.confidence : 'medium') as 'high' | 'medium' | 'low';
            
            // Human review recommended if confidence is medium/low OR score is in the bottom half of scale
            const isBottomHalf = score !== null && score <= ((scaleMin + scaleMax) / 2);
            const isLowerConfidence = confidence === 'low' || confidence === 'medium';
            const humanReviewRecommended = isBottomHalf || isLowerConfidence;

            return {
              promptId,
              promptText,
              domain,
              responseText,
              rubricNotes,
              aiEstimatedScore: score,
              rubricScale: data.rubricScale || `${scaleMin}-${scaleMax} holistic`,
              scaleMin,
              scaleMax,
              humanReviewRecommended,
              aiJustification: data.justification || 'Score awarded based on rubric criteria evaluation.',
              confidence,
              strengths: Array.isArray(data.strengths) ? data.strengths : [],
              weaknesses: Array.isArray(data.weaknesses) ? data.weaknesses : [],
              actionableFeedback: data.actionableFeedback || 'Review rubric benchmarks.'
            };
          }
        } catch (apiErr) {
          console.error(`Error calling AI CR evaluator for prompt ${promptId}:`, apiErr);
        }

        // Fallback if AI call failed
        return {
          promptId,
          promptText,
          domain,
          responseText,
          rubricNotes,
          aiEstimatedScore: null,
          rubricScale: '0-4 holistic',
          humanReviewRecommended: true,
          aiJustification: 'AI scoring service temporarily unavailable. Human review recommended.'
        };
      })
    );
  }

  const reportId = `report_${mockAttempt.id}`;
  const report: UnofficialScoreReport = {
    id: reportId,
    mockAttemptId: mockAttempt.id,
    userId: mockAttempt.userId,
    mockExamId: mockExam.id,
    paperId: mockAttempt.paperId,
    generatedAt: new Date().toISOString(),
    rawScore: {
      correct: totalCorrect,
      total: totalQuestions,
      percentCorrect
    },
    domainBreakdown,
    estimatedScaledScore,
    scoreScaleMin: minScale ?? null,
    scoreScaleMax: maxScale ?? null,
    realPassingScore: passingScore ?? null,
    estimatedReadiness,
    constructedResponseEstimates,
    disclaimerAcknowledgedAt: null
  };

  try {
    const reportRef = doc(db, 'unofficialScoreReports', reportId);
    await setDoc(reportRef, report);
  } catch (err) {
    console.warn(`Error writing UnofficialScoreReport ${reportId} to Firestore:`, err);
  }

  return report;
}

/**
 * Retrieves an UnofficialScoreReport by attempt ID or report ID.
 */
export async function getUnofficialScoreReport(reportIdOrAttemptId: string): Promise<UnofficialScoreReport | null> {
  try {
    // Check by doc ID first (which is report_{attemptId} or custom ID)
    const exactRef = doc(db, 'unofficialScoreReports', reportIdOrAttemptId);
    const snap = await getDoc(exactRef);
    if (snap.exists()) {
      return { id: snap.id, ...snap.data() } as UnofficialScoreReport;
    }

    const prefixedRef = doc(db, 'unofficialScoreReports', `report_${reportIdOrAttemptId}`);
    const prefSnap = await getDoc(prefixedRef);
    if (prefSnap.exists()) {
      return { id: prefSnap.id, ...prefSnap.data() } as UnofficialScoreReport;
    }

    // Query by mockAttemptId
    const colRef = collection(db, 'unofficialScoreReports');
    const q = query(colRef, where('mockAttemptId', '==', reportIdOrAttemptId));
    const querySnap = await getDocs(q);
    if (!querySnap.empty) {
      return { id: querySnap.docs[0].id, ...querySnap.docs[0].data() } as UnofficialScoreReport;
    }
  } catch (err) {
    console.warn('Error fetching unofficial score report:', err);
  }
  return null;
}

/**
 * Acknowledges the score report disclaimer and persists the timestamp.
 */
export async function acknowledgeReportDisclaimer(reportId: string): Promise<string> {
  const nowIso = new Date().toISOString();
  try {
    const reportRef = doc(db, 'unofficialScoreReports', reportId);
    await setDoc(reportRef, { disclaimerAcknowledgedAt: nowIso }, { merge: true });
  } catch (err) {
    console.warn(`Error acknowledging disclaimer for report ${reportId}:`, err);
  }
  return nowIso;
}

